import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Day13, Calendar13Config } from '@/lib/calendar13Types';
import { day13ToGregorian } from '@/lib/calendar13';
import type { CycleRing } from '@/lib/cycleTypes';
import type { CycleAlignment } from '@/lib/cycleConfig';
import { createCycleFromPreset } from '@/lib/cycleConfig';
import type { RingTheme, RingThemeState } from '@/lib/ringThemeStore';
import { toast } from '@/hooks/use-toast';
import { clampDayOfYear } from './dashboardHooksShared';

export function useDashboardCycles({
  isAdmin,
  isManualMode,
  config,
  selectedYear,
  selectedMonth,
  setSelectedMonth,
  selectedDay,
  selectDay,
  today,
  shownTime,
  dayOfYear,
  displayTime,
  currentTime,
  cycleRings,
  setCycleRings,
  hiddenRingsRef,
  setHiddenRings,
  effectiveVisibleRingCount,
  bumpVisibleRingCount,
  saveCyclesNow,
  syncCyclesToDateTime,
  setShowCycleWizard,
  setAlignments,
  ringThemeState,
  updateRingThemeState,
  getStageFromRotation,
  getDayOfYear,
}: {
  isAdmin: boolean;
  isManualMode: boolean;
  config: Calendar13Config;
  selectedYear: number;
  selectedMonth: number;
  setSelectedMonth: (month: number) => void;
  selectedDay: Day13 | null;
  selectDay: (day: Day13) => void;
  today: Day13;
  shownTime: Date;
  dayOfYear: number;
  displayTime: Date | null;
  currentTime: Date;

  cycleRings: CycleRing[];
  setCycleRings: (next: CycleRing[] | ((prev: CycleRing[]) => CycleRing[])) => void;
  hiddenRingsRef: RefObject<string[]>;
  setHiddenRings: (next: string[] | ((prev: string[]) => string[])) => void;
  effectiveVisibleRingCount?: number;

  bumpVisibleRingCount: (nextCount: number) => void;
  saveCyclesNow: (ringsOverride?: CycleRing[], hiddenOverride?: string[]) => Promise<void> | void;

  syncCyclesToDateTime: (dateTime: Date) => void;

  setShowCycleWizard: (open: boolean) => void;
  setAlignments: (next: CycleAlignment[] | ((prev: CycleAlignment[]) => CycleAlignment[])) => void;

  ringThemeState: RingThemeState;
  updateRingThemeState: (next: RingThemeState | ((prev: RingThemeState) => RingThemeState)) => void;

  getStageFromRotation: (rotation: number, stages: number) => number;
  getDayOfYear: (day: Day13) => number;
}) {
  const handleAddCycle = useCallback(
    (cycle: CycleRing) => {
      if (!isAdmin) return;
      setCycleRings((prev) => {
        const next = [...prev, cycle];
        bumpVisibleRingCount(next.length);
        void saveCyclesNow(next, hiddenRingsRef.current);
        return next;
      });
      toast.success(`Added ${cycle.name} cycle`);
    },
    [bumpVisibleRingCount, hiddenRingsRef, isAdmin, saveCyclesNow, setCycleRings]
  );

  const handleUpdateCycle = useCallback(
    (cycle: CycleRing) => {
      if (!isAdmin) return;
      setCycleRings((prev) => {
        const next = prev.map((ring) => (ring.id === cycle.id ? cycle : ring));
        void saveCyclesNow(next, hiddenRingsRef.current);
        return next;
      });
      toast.success(`Updated ${cycle.name}`);
    },
    [hiddenRingsRef, isAdmin, saveCyclesNow, setCycleRings]
  );

  const handleRemoveCycle = useCallback(
    (id: string) => {
      if (!isAdmin) return;
      setCycleRings((prev) => {
        const nextRings = prev.filter((ring) => ring.id !== id);
        const nextHidden = hiddenRingsRef.current.filter((hid) => hid !== id);
        void saveCyclesNow(nextRings, nextHidden);
        return nextRings;
      });
      setHiddenRings((prev) => prev.filter((hid) => hid !== id));
      toast.success('Removed cycle');
    },
    [hiddenRingsRef, isAdmin, saveCyclesNow, setCycleRings, setHiddenRings]
  );

  const [editingCycle, setEditingCycle] = useState<CycleRing | null>(null);
  const visibleRingRecencyRef = useRef<string[]>([]);

  useEffect(() => {
    const visibleIds = cycleRings
      .map((ring) => ring?.id)
      .filter((id): id is string => Boolean(id) && !hiddenRingsRef.current.includes(id));
    const previous = visibleRingRecencyRef.current;
    const preserved = previous.filter((id) => visibleIds.includes(id));
    const appended = visibleIds.filter((id) => !preserved.includes(id));
    visibleRingRecencyRef.current = [...preserved, ...appended];
  }, [cycleRings, hiddenRingsRef]);

  const handleEditCycle = useCallback(
    (cycle: CycleRing) => {
      if (!isAdmin) return;
      try {
        const safeCycle: CycleRing = {
          ...cycle,
          labels: Array.isArray(cycle.labels) ? [...cycle.labels] : [],
          colors: Array.isArray(cycle.colors) ? [...cycle.colors] : [],
        };
        setEditingCycle(safeCycle);
        setShowCycleWizard(true);
      } catch (err) {
        console.error('handleEditCycle error', err, cycle);
        try {
          setEditingCycle(null);
          setShowCycleWizard(false);
        } catch {
          // ignore
        }
      }
    },
    [isAdmin, setShowCycleWizard]
  );

  const handleAddAlignment = useCallback(
    (alignment: CycleAlignment) => {
      // Allow all users to edit their own alignments (relationships)
      // Admins: changes saved to global settings for all users
      // Non-admins: changes saved to personal settings only
      let changed = false;
      setAlignments((prev) => {
        const matchIndex = prev.findIndex(
          (existing) =>
            (existing.sourceCycleId === alignment.sourceCycleId &&
              existing.targetCycleId === alignment.targetCycleId) ||
            (existing.sourceCycleId === alignment.targetCycleId &&
              existing.targetCycleId === alignment.sourceCycleId)
        );
        if (matchIndex === -1) {
          changed = true;
          return [...prev, alignment];
        }

        const existing = prev[matchIndex];
        const isSame =
          existing.sourceCycleId === alignment.sourceCycleId &&
          existing.targetCycleId === alignment.targetCycleId &&
          existing.sourceStage === alignment.sourceStage &&
          existing.targetStage === alignment.targetStage &&
          existing.sourceSegments === alignment.sourceSegments &&
          existing.targetSegments === alignment.targetSegments &&
          existing.name === alignment.name &&
          existing.controlMode === alignment.controlMode;

        if (isSame) {
          return prev;
        }

        changed = true;
        return prev.map((item, index) => (index === matchIndex ? alignment : item));
      });

      if (!changed) {
        return;
      }
      
      // Different toast for admins vs regular users
      if (isAdmin) {
        toast({
          title: 'Alignment added (Global)',
          description: 'This change affects all users. Non-admins have personal settings.',
          variant: 'default',
        });
      } else {
        toast.success('Alignment added to your settings');
      }
    },
    [setAlignments, isAdmin]
  );

  const handleApplyAlignment = useCallback(
    (newRings: CycleRing[]) => {
      // Allow all users to apply alignments to their own cycles
      setCycleRings(newRings);
      requestAnimationFrame(() => {
        syncCyclesToDateTime(displayTime ?? currentTime);
      });
      // interlocking now saved via cycleStore (parentId and stagesPerParent on each ring)
      void saveCyclesNow(newRings, hiddenRingsRef.current);
    },
    [currentTime, displayTime, setCycleRings, syncCyclesToDateTime, saveCyclesNow, hiddenRingsRef]
  );

  const handleToggleRing = useCallback(
    (ringId: string) => {
      setHiddenRings((prev) => {
        const isHidden = prev.includes(ringId);
        const visibleIds = cycleRings
          .map((ring) => ring?.id)
          .filter((id): id is string => Boolean(id) && !prev.includes(id));

        if (!isHidden) {
          visibleRingRecencyRef.current = visibleRingRecencyRef.current.filter((id) => id !== ringId);
          const nextHidden = [...prev, ringId];
          void saveCyclesNow(cycleRings, nextHidden);
          return nextHidden;
        }

        const maxVisible = Math.max(
          1,
          Math.min(
            typeof effectiveVisibleRingCount === 'number' ? effectiveVisibleRingCount : cycleRings.length,
            cycleRings.length,
          ),
        );
        const nextVisibleIds = Array.from(new Set([...visibleIds, ringId]));
        const baseRecency = visibleRingRecencyRef.current.filter((id) => nextVisibleIds.includes(id) && id !== ringId);
        const nextRecency = [...baseRecency, ringId];
        const overflow = Math.max(0, nextRecency.length - maxVisible);
        const idsToHide = overflow > 0 ? nextRecency.slice(0, overflow) : [];
        const hiddenSet = new Set(prev.filter((id) => id !== ringId));
        idsToHide.forEach((id) => hiddenSet.add(id));
        const nextHidden = Array.from(hiddenSet);
        visibleRingRecencyRef.current = nextRecency.filter((id) => !idsToHide.includes(id));
        void saveCyclesNow(cycleRings, nextHidden);
        return nextHidden;
      });
    },
    [cycleRings, effectiveVisibleRingCount, saveCyclesNow, setHiddenRings]
  );

  const handleReorderRings = useCallback(
    (orderedIds: string[]) => {
      if (!isAdmin) return;
      setCycleRings((prev) => {
        const orderMap = new Map(orderedIds.map((id, index) => [id, index]));
        return prev.map((ring) => ({
          ...ring,
          displayOrder: orderMap.get(ring.id) ?? ring.displayOrder,
        }));
      });
    },
    [isAdmin, setCycleRings]
  );

  const handleSetSkinMode = useCallback(
    (ringId: string, mode: CycleRing['skinMode'] | null) => {
      if (!isAdmin) return;
      setCycleRings((prev) =>
        prev.map((ring) => {
          if (ring.id === ringId) {
            return { ...ring, skinMode: mode ?? undefined };
          }
          if (mode && ring.skinMode === mode) {
            return { ...ring, skinMode: undefined };
          }
          return ring;
        })
      );
    },
    [isAdmin, setCycleRings]
  );

  const handleRingsChange = useCallback(
    (newVisibleRings: CycleRing[]) => {
      setCycleRings((prevRings) => {
        const visibleIds = new Set(newVisibleRings.map((ring) => ring.id));
        const hiddenRingsData = prevRings.filter((ring) => !visibleIds.has(ring.id));
        const next = [...newVisibleRings, ...hiddenRingsData];
        if (isAdmin) {
          void saveCyclesNow(next, hiddenRingsRef.current);
        }
        return next;
      });
    },
    [isAdmin, setCycleRings, saveCyclesNow, hiddenRingsRef]
  );

  const handleAddRingFromPreset = useCallback(
    (presetId: string) => {
      if (!isAdmin) return;
      if (cycleRings.some((ring) => ring.id === presetId)) {
        toast.error('This cycle is already added');
        return;
      }
      const newCycle = createCycleFromPreset(presetId, dayOfYear);
      if (newCycle) {
        const maxOrder = Math.max(...cycleRings.map((ring) => ring.displayOrder ?? 0), -1);
        setCycleRings((prev) => {
          const next = [...prev, { ...newCycle, displayOrder: maxOrder + 1 }];
          bumpVisibleRingCount(next.length);
          void saveCyclesNow(next, hiddenRingsRef.current);
          return next;
        });
        toast.success(`Added ${newCycle.name}`);
      }
    },
    [bumpVisibleRingCount, cycleRings, dayOfYear, hiddenRingsRef, isAdmin, saveCyclesNow, setCycleRings]
  );

  const liveGalacticDay = useMemo(() => {
    const daysRing = cycleRings.find((ring) => ring.id === 'days');
    const moonsRing = cycleRings.find((ring) => ring.id === 'moons');

    if (daysRing && moonsRing) {
      const dayStage = getStageFromRotation(daysRing.rotation, daysRing.stages);
      const monthStage = getStageFromRotation(moonsRing.rotation, moonsRing.stages);

      const daySegment = 360 / daysRing.stages;
      const normalizedDays = (((-daysRing.rotation) % 360) + 360) % 360;
      const fractionOfDay = (normalizedDays - dayStage * daySegment) / daySegment;

      const dayInMonth = dayStage + 1;
      const month = monthStage + 1;

      const dayOfYr = Math.min(365, (month - 1) * 28 + dayInMonth);
      const isDrift = dayOfYr > 364;

      const hours = Math.floor(fractionOfDay * 24);
      const minutes = Math.floor((fractionOfDay * 24 - hours) * 60);
      const seconds = Math.floor(((fractionOfDay * 24 - hours) * 60 - minutes) * 60);

      const gregorianDate = day13ToGregorian(selectedYear, isDrift ? 0 : month, isDrift ? 0 : dayInMonth, config);
      gregorianDate.setHours(hours, minutes, seconds);

      return {
        month: isDrift ? 0 : month,
        day: isDrift ? 0 : dayInMonth,
        year: selectedYear,
        isOutOfTime: isDrift,
        gregorianDate,
        weekday: isDrift ? -1 : (dayInMonth - 1) % 7,
        hours,
        minutes,
        seconds,
      };
    }

    return null;
  }, [config, cycleRings, getStageFromRotation, selectedYear]);

  // Keep selection aligned to live day when syncing
  useEffect(() => {
    if (!liveGalacticDay || isManualMode) return;

    selectDay(liveGalacticDay);

    if (liveGalacticDay.isOutOfTime) {
      setSelectedMonth(14);
    } else {
      setSelectedMonth(liveGalacticDay.month);
    }
  }, [isManualMode, liveGalacticDay, selectDay, setSelectedMonth]);

  const indicatorDay = useMemo(() => {
    if (liveGalacticDay && !liveGalacticDay.isOutOfTime) {
      return liveGalacticDay;
    }

    if (selectedDay && !selectedDay.isOutOfTime) {
      return {
        ...selectedDay,
        gregorianDate:
          selectedDay.gregorianDate || day13ToGregorian(selectedDay.year, selectedDay.month, selectedDay.day, config),
      };
    }

    return null;
  }, [config, liveGalacticDay, selectedDay]);

  const shouldUseLiveDay = Boolean(!isManualMode && liveGalacticDay);
  const displayDay: Day13 = shouldUseLiveDay ? (liveGalacticDay as Day13) : selectedDay || today;

  const waveformDayOfYear = useMemo(() => {
    const baseDayOfYear = shouldUseLiveDay
      ? liveGalacticDay!.isOutOfTime
        ? 365
        : (liveGalacticDay!.month - 1) * 28 + liveGalacticDay!.day
      : getDayOfYear(selectedDay || today);
    const secondsIntoDay = shownTime.getHours() * 3600 + shownTime.getMinutes() * 60 + shownTime.getSeconds();
    return baseDayOfYear + secondsIntoDay / 86400;
  }, [getDayOfYear, liveGalacticDay, selectedDay, shownTime, shouldUseLiveDay, today]);

  const currentGregorianDate = useMemo(() => {
    const isValidDate = (value: Date | null | undefined) => value instanceof Date && !Number.isNaN(value.getTime());

    try {
      // In live mode, use the actual system time (currentTime), not the cycle-computed time
      if (!isManualMode) {
        return new Date(currentTime);
      }

      // If the UI is in manual mode and we have an explicit displayTime, prefer that directly when valid.
      if (isValidDate(displayTime)) return new Date(displayTime as Date);

      // Fall back to the selected day (or today) and stitch on the currently shown time.
      const day = selectedDay || today;
      if (!day || typeof day.year !== 'number' || typeof day.month !== 'number' || typeof day.day !== 'number') {
        return new Date(); // Fallback to current time
      }

      const baseDate = day13ToGregorian(day.year, day.month, day.day, config);
      if (!isValidDate(baseDate)) {
        return new Date(); // Fallback to current time
      }

      const date = new Date(baseDate);
      date.setHours(shownTime.getHours(), shownTime.getMinutes(), shownTime.getSeconds());
      return isValidDate(date) ? date : new Date();
    } catch (error) {
      console.error('[useDashboardCycles] Error calculating currentGregorianDate:', error);
      return new Date(); // Fallback to current time
    }
  }, [config, currentTime, displayTime, isManualMode, selectedDay, shownTime, today]);

  const activeRingTheme = useMemo(
    () => ringThemeState.themes.find((theme) => theme.id === ringThemeState.activeThemeId) ?? null,
    [ringThemeState]
  );

  const cycleDisplayMode = ringThemeState.cycleDisplayMode === 'single' ? 'single' : 'multi';

  const handleSaveRingTheme = useCallback(
    (theme: RingTheme) => {
      updateRingThemeState((prev) => {
        const existingIndex = prev.themes.findIndex((entry) => entry.id === theme.id);
        const nextThemes =
          existingIndex === -1
            ? [...prev.themes, theme]
            : prev.themes.map((entry) => (entry.id === theme.id ? theme : entry));
        return { ...prev, themes: nextThemes };
      });
      toast.success(`Saved ${theme.name}`);
    },
    [updateRingThemeState]
  );

  const handleDeleteRingTheme = useCallback(
    (themeId: string) => {
      updateRingThemeState((prev) => ({
        ...prev,
        themes: prev.themes.filter((theme) => theme.id !== themeId),
        activeThemeId: prev.activeThemeId === themeId ? undefined : prev.activeThemeId,
      }));
      setCycleRings((prev) => prev.map((ring) => (ring.themeId === themeId ? { ...ring, themeId: undefined } : ring)));
      toast.success('Theme deleted');
    },
    [setCycleRings, updateRingThemeState]
  );

  const handleApplyRingThemeLayout = useCallback(
    (themeId: string) => {
      updateRingThemeState((prev) => ({ ...prev, activeThemeId: themeId }));
      const theme = ringThemeState.themes.find((entry) => entry.id === themeId);
      if (theme?.name) {
        toast.success(`Applied ${theme.name} layout`);
      }
    },
    [ringThemeState.themes, updateRingThemeState]
  );

  const handleApplyRingThemeToRing = useCallback(
    (themeId: string, ringId: string) => {
      const theme = ringThemeState.themes.find((entry) => entry.id === themeId);
      if (!theme) return;
      setCycleRings((prev) =>
        prev.map((ring) =>
          ring.id === ringId ? { ...ring, colors: theme.colors, themeId: theme.id } : ring
        )
      );
      toast.success(`Applied ${theme.name} to ring`);
    },
    [ringThemeState.themes, setCycleRings]
  );

  const handleApplyRingThemeOrder = useCallback(
    (themeId: string) => {
      const theme = ringThemeState.themes.find((entry) => entry.id === themeId);
      if (!theme?.ringOrder?.length) return;
      const orderMap = new Map(theme.ringOrder.map((id, index) => [id, index]));
      setCycleRings((prev) =>
        prev.map((ring) => ({
          ...ring,
          displayOrder: orderMap.get(ring.id) ?? ring.displayOrder,
        }))
      );
      toast.success(`Applied ${theme.name} ring order`);
    },
    [ringThemeState.themes, setCycleRings]
  );

  const handleCycleDisplayModeChange = useCallback(
    (mode: 'single' | 'multi') => {
      updateRingThemeState((prev) => ({
        ...prev,
        cycleDisplayMode: mode,
      }));
    },
    [updateRingThemeState]
  );

  return {
    editingCycle,
    setEditingCycle,
    handleAddCycle,
    handleUpdateCycle,
    handleRemoveCycle,
    handleEditCycle,
    handleAddAlignment,
    handleApplyAlignment,
    handleToggleRing,
    handleReorderRings,
    handleSetSkinMode,
    handleRingsChange,
    handleAddRingFromPreset,
    liveGalacticDay,
    indicatorDay,
    shouldUseLiveDay,
    displayDay,
    waveformDayOfYear,
    currentGregorianDate,
    activeRingTheme,
    cycleDisplayMode,
    handleSaveRingTheme,
    handleDeleteRingTheme,
    handleApplyRingThemeLayout,
    handleApplyRingThemeToRing,
    handleApplyRingThemeOrder,
    handleCycleDisplayModeChange,
  };
}
